import * as signalR from "@microsoft/signalr";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import Auth from "./Auth";
import { getApiBaseUrl } from "@/common/utils";

export default class SignalRHubConnection {
  protected connection: HubConnection;

  private _hubName: string;
  private _connectionStateChangedListeners = [];
  private starting: Promise<void> | null = null;
  private stopping: Promise<void> | null = null;
  private handlersRegistered = false;

  constructor(hubName: string) {
    this._hubName = hubName;
    this.connection = new signalR.HubConnectionBuilder()
      .withAutomaticReconnect()
      .withUrl(`${getApiBaseUrl()}hubs/${this._hubName}`, {
        accessTokenFactory: () => Auth.getAccessToken(),
      })
      .build();
  }

  public async init(): Promise<void> {
    if (this.stopping) await this.stopping;
    if (this.starting) return this.starting;
    if (this.connection.state !== HubConnectionState.Disconnected) {
      this.onConnectionStateChanged(this.connection.state);
      return;
    }
    if (!this.handlersRegistered) {
      this.handlersRegistered = true;
      this.connection.onreconnecting((_err) => {
        this.onConnectionStateChanged(this.connection.state);
      });
      this.connection.onreconnected(() => {
        this.onConnectionStateChanged(this.connection.state);
      });
      this.connection.onclose(() => this.onConnectionStateChanged(this.connection.state));
    }
    this.starting = this.connection.start();
    try {
      await this.starting;
      this.onConnectionStateChanged(this.connection.state);
    } finally { this.starting = null; }
  }

  public stop(): Promise<void> {
    if (!this.stopping) {
      this.stopping = (async () => {
        try { await this.starting; } catch { /* A failed start still needs cleanup. */ }
        await this.connection.stop();
      })().finally(() => { this.stopping = null; });
    }
    return this.stopping;
  }

  public addConnectionChangedListener(
    callback: (state: HubConnectionState) => void
  ): void {
    this._connectionStateChangedListeners.push(callback);
  }

  public removeConnectionChangedListener(
    callback: (state: HubConnectionState) => void
  ): void {
    const index = this._connectionStateChangedListeners.indexOf(callback);
    if (index < 0) {
      return;
    }
    this._connectionStateChangedListeners.splice(index);
  }

  private onConnectionStateChanged(state: HubConnectionState): void {
    if (this._connectionStateChangedListeners.length <= 0) {
      return;
    }
    for (const listener of this._connectionStateChangedListeners) {
      listener(state);
    }
  }
}
